/* eslint-disable import/no-unresolved */

/**
 * Spis tresci wpisu blogowego.
 *
 * Tresc przychodzi z edytora jako gotowy HTML, wiec nie znamy naglowkow
 * na etapie szablonu - budujemy spis po stronie przegladarki z naglowkow H2,
 * ktore po drodze dostaja identyfikatory do linkowania.
 *
 * Wpis potrafi miec kilka blokow tresci (Content, RawHtml) poprzeplatanych
 * innymi blokami, wiec naglowki zbieramy ze wszystkich naraz - spis ma
 * opisywac caly artykul, a nie tylko jego pierwszy kawalek.
 */

const CONTENT_SELECTOR = '.blog-post-content';
const HEADING_SELECTOR = 'h2';
const DESKTOP_QUERY = '(min-width: 992px)';

// Bloki wypuszczone poza kolumne tekstu (listingi, profil autora) nie sa trescia
// wpisu - ich naglowki nie trafiaja do spisu, choc profil autora tez opakowuje
// swoje bio w .blog-post-content.
const WIDE_SELECTOR = '.blog-post-wide';

// Naglowek strony jest przyklejony, a nad trescia potrafi stanac jeszcze pasek
// promocyjny - obie wysokosci mierzymy, bo zaleza od rynku i kampanii.
const STICKY_SELECTORS = ['.main-header', '.promotion-topbar-sticky'];
const STICKY_GAP = 24;

/**
 * Wysokosc tego, co przykleja sie u gory okna. Element, ktory akurat nie jest
 * przyklejony (np. pasek promocyjny na telefonie), nie zaslania tresci, wiec
 * go nie liczymy.
 */
const stickyOffset = () =>
    STICKY_SELECTORS.reduce((offset, selector) => {
        const element = document.querySelector(selector);

        if (element === null) {
            return offset;
        }

        const { position } = window.getComputedStyle(element);

        if (position !== 'sticky' && position !== 'fixed') {
            return offset;
        }

        return offset + element.offsetHeight;
    }, 0);

/**
 * Zamienia tytul naglowka na identyfikator. Polskie znaki rozkladamy na
 * podstawowe litery, zeby kotwica byla czytelna w adresie.
 */
const slugify = (text) =>
    text
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/ł/g, 'l')
        .replace(/Ł/g, 'L')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

/**
 * Bloki tresci wpisu w kolejnosci, w jakiej stoja na stronie.
 */
const contentRoots = () =>
    Array.from(document.querySelectorAll(CONTENT_SELECTOR)).filter(
        (root) => root.closest(WIDE_SELECTOR) === null
    );

/**
 * Nadaje naglowkom identyfikatory i zwraca liste pozycji spisu.
 * Powtorzone tytuly dostaja numer, zeby kotwice byly unikalne - takze wtedy,
 * gdy powtarzaja sie miedzy osobnymi blokami tresci.
 */
const collectHeadings = (roots) => {
    const used = new Set();

    return roots
        .reduce(
            (headings, root) =>
                headings.concat(Array.from(root.querySelectorAll(HEADING_SELECTOR))),
            []
        )
        .filter((heading) => heading.textContent.trim() !== '')
        .map((heading) => {
            const label = heading.textContent.trim();
            let id = heading.id || slugify(label);

            if (id === '') {
                id = 'sekcja';
            }

            let unique = id;
            let suffix = 2;

            while (used.has(unique)) {
                unique = `${id}-${suffix}`;
                suffix += 1;
            }

            used.add(unique);
            heading.id = unique;

            return { id: unique, label, element: heading };
        });
};

/**
 * Rozklada tresc wpisu na dwie kolumny: szyne ze spisem po lewej i wlasciwa
 * tresc po prawej. Uklad zaczyna sie na pierwszym bloku tresci i obejmuje
 * wszystko az do pierwszego bloku szerokiego (powiazane wpisy, profil autora) -
 * te zostaja poza nim, bo centruja sie wzgledem okna, a nie kolumny.
 *
 * Robimy to w JS, bo spis tresci tez powstaje dopiero w przegladarce - bez
 * niego szyna nie mialaby czego trzymac.
 */
const buildLayout = (content, toc) => {
    const layout = document.createElement('div');
    layout.className = 'blog-post-layout';

    const rail = document.createElement('aside');
    rail.className = 'blog-post-layout__rail';
    rail.appendChild(toc);

    const main = document.createElement('div');
    main.className = 'blog-post-layout__main';

    content.parentNode.insertBefore(layout, content);
    layout.appendChild(rail);
    layout.appendChild(main);

    let node = layout.nextSibling;

    while (node !== null) {
        const isWide = node.nodeType === Node.ELEMENT_NODE && node.matches(WIDE_SELECTOR);

        if (isWide) {
            break;
        }

        const next = node.nextSibling;
        main.appendChild(node);
        node = next;
    }
};

const buildToc = (headings) => {
    const details = document.createElement('details');
    details.className = 'blog-post-toc';

    const summary = document.createElement('summary');
    summary.className = 'blog-post-toc__summary';
    summary.textContent = 'W tym artykule';
    details.appendChild(summary);

    const list = document.createElement('ol');
    list.className = 'blog-post-toc__list';

    headings.forEach((heading) => {
        const item = document.createElement('li');
        item.className = 'blog-post-toc__item';
        item.dataset.tocFor = heading.id;

        const link = document.createElement('a');
        link.className = 'blog-post-toc__link';
        link.href = `#${heading.id}`;
        link.textContent = heading.label;

        item.appendChild(link);
        list.appendChild(item);
    });

    details.appendChild(list);

    return details;
};

/**
 * Podswietla pozycje sekcji, w ktorej czytelnik akurat jest.
 *
 * Aktywny jest ostatni naglowek, ktory minal linie pod przyklejonym menu -
 * te sama, na ktorej zatrzymuje sie kotwica po kliknieciu w spis. Zanim minie
 * ja pierwszy naglowek, zaznaczamy pierwsza pozycje, zeby spis nigdy nie byl
 * pusty: na samej gorze wpisu zaden naglowek nie jest jeszcze pod linia,
 * a czytelnik jest juz w pierwszej sekcji.
 */
const trackActiveHeading = (headings, toc) => {
    const items = new Map(
        Array.from(toc.querySelectorAll('.blog-post-toc__item')).map((item) => [
            item.dataset.tocFor,
            item,
        ])
    );

    let active = null;

    const setActive = (id) => {
        if (id === active) {
            return;
        }

        active = id;

        items.forEach((item, key) => {
            if (key === id) {
                item.setAttribute('aria-current', 'true');
            } else {
                item.removeAttribute('aria-current');
            }
        });
    };

    // Tolerancja jednego piksela - po kliknieciu w spis naglowek zatrzymuje sie
    // dokladnie na linii, a pozycje bywaja ulamkowe.
    const activeId = () => {
        const line = stickyOffset() + STICKY_GAP + 1;
        const passed = headings.filter(
            (heading) => heading.element.getBoundingClientRect().top <= line
        );

        return passed.length > 0 ? passed[passed.length - 1].id : headings[0].id;
    };

    // Przy przewijaniu liczymy raz na klatke - pozycji naglowkow nie da sie
    // odczytac taniej, a przy kazdym zdarzeniu scrolla byloby ich za duzo.
    let frame = null;

    const schedule = () => {
        if (frame !== null) {
            return;
        }

        frame = window.requestAnimationFrame(() => {
            frame = null;
            setActive(activeId());
        });
    };

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    // Zdjecia doladowuja sie po starcie i przesuwaja naglowki, wiec po `load`
    // przeliczamy jeszcze raz.
    window.addEventListener('load', schedule);

    setActive(activeId());
};

const initToc = () => {
    const roots = contentRoots();

    if (roots.length === 0) {
        return;
    }

    const headings = collectHeadings(roots);

    // Spis ma sens dopiero przy kilku sekcjach - przy jednej tylko zaśmieca.
    if (headings.length < 2) {
        return;
    }

    const toc = buildToc(headings);

    // Na duzym ekranie spis jest rozwiniety na stale, na telefonie zwiniety,
    // zeby nie odpychal tresci na kolejny ekran. Przy zmianie szerokosci okna
    // wracamy do stanu wlasciwego dla nowego ukladu - na desktopie naglowek
    // spisu nie jest juz przelacznikiem, wiec nie ma czym go rozwinac.
    const desktop = window.matchMedia(DESKTOP_QUERY);

    toc.open = desktop.matches;
    desktop.addEventListener('change', (event) => {
        toc.open = event.matches;
    });

    // Spis stoi w lewej kolumnie obok tresci i podaza za nia przy przewijaniu
    // (na telefonie kolumny nie ma - spis laduje zwiniety nad trescia).
    buildLayout(roots[0], toc);
    trackActiveHeading(headings, toc);

    // Spis przypinamy pod przyklejonym naglowkiem - tej samej wartosci uzywa
    // odstep dla kotwic, zeby klikniety naglowek nie chowal sie pod menu.
    const article = roots[0].closest('.blog-post');

    const applyOffset = () => {
        if (article !== null) {
            article.style.setProperty('--blog-sticky-offset', `${stickyOffset() + STICKY_GAP}px`);
        }
    };

    applyOffset();
    desktop.addEventListener('change', applyOffset);
    window.addEventListener('load', applyOffset);
};

/**
 * Blok RelatedProducts z edytora Puck.
 *
 * Szablon zostawia sam kontener z ustawieniami bloku, a produkty dobiera
 * przegladarka, wiec kafelki montuje tu komponent Vue. Kod aplikacji sciagamy
 * dopiero, gdy blok zbliza sie do ekranu - listing kategorii to spore zapytanie,
 * a blok potrafi stac na koncu dlugiego wpisu.
 */
const RELATED_PRODUCTS_SELECTOR = '.js-vue-blog-related-products';

const mountRelatedProducts = async (el) => {
    // Rzut w setup jednej aplikacji Vue zabija wszystkie montowane po niej,
    // wiec kazdy blok montujemy osobno i nie pozwalamy bledowi wyjsc dalej.
    if (el.__vue_app__) {
        return;
    }

    try {
        const [{ h }, { createVueApp }, { sharedPinia }, { default: BlogRelatedProducts }] =
            await Promise.all([
                import('vue'),
                import('@vuejs/entry'),
                import('@vuejs/pinia'),
                import(
                    /* webpackChunkName: "BlogRelatedProducts" */ '@vuejs/components/blog/BlogRelatedProducts.vue'
                ),
            ]);

        // createVueApp nie przyjmuje propsow roota, wiec owijamy komponent renderem.
        const app = createVueApp({
            render: () =>
                h(BlogRelatedProducts, {
                    categoryId: el.dataset.categoryId,
                    limit: Number(el.dataset.limit) || 4,
                    sort: el.dataset.sort || 'popularity',
                    heading: el.dataset.heading || '',
                    ctaLabel: el.dataset.ctaLabel || '',
                }),
        });

        // AsyncProductItem w srodku siega po useAuthStore.
        app.use(sharedPinia);

        app.mount(el);
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[blog] Nie udało się zamontować bloku RelatedProducts', err);
    }
};

const initRelatedProducts = () => {
    const mounts = document.querySelectorAll(RELATED_PRODUCTS_SELECTOR);

    if (!mounts.length) {
        return;
    }

    if (!('IntersectionObserver' in window)) {
        mounts.forEach(mountRelatedProducts);

        return;
    }

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) {
                    return;
                }

                observer.unobserve(entry.target);
                mountRelatedProducts(entry.target);
            });
        },
        { rootMargin: '600px 0px' }
    );

    mounts.forEach((el) => observer.observe(el));
};

initToc();
initRelatedProducts();
